import Table from "../common/Table/Table";
import { applicationsTableColumns } from "../../config/tableColumns";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/ApiService";
import { getStoredItem, setStoredItem } from "../../config/storageUtils";
import { incentiveClaimTypesList } from "../../config/applicationStatusConfig";
import { formatDate } from "../../utils/utils";
import ConfirmDialog from "../common/ConfirmDialog";
import Message from "../common/Message";
import { installmentsList } from "../../config/appliedSchemesConfig";
import ApplicationPrint from "../common/ApplicationPrint";
import Generating from "../common/Generating";
import { useLanguage } from "../LanguageProvider/LanguageProvider";

const Applications = ({ title, status }) => {
  const [applications, setApplications] = useState([]);
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [apiMessage, setApiMessage] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);
  const pdfRef = useRef(null);
  const { language, setLanguage } = useLanguage();
  const [userDetails, setUserDetails] = useState({});
  const [pdfData, setPdfData] = useState(null);

  useEffect(() => {
    const getUserDetails = async () => {
      const resp = await api.get("/user-full-data");
      if (resp.status === 200) {
        setUserDetails(resp.data);
      }
    };
    getUserDetails();
  }, []);

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const response = await api.get(`/get-user-applications`);
        const data = response.data || [];

        // Create lookup maps for faster access
        const statusMap = Object.fromEntries(
          status.map((s) => [s.value, language ? s.hi : s.label]),
        );
        const claimTypeMap = Object.fromEntries(
          incentiveClaimTypesList.map((t) => [
            String(t.value),
            language ? t.hi : t.label,
          ]),
        );
        const installmentMap = Object.fromEntries(
          installmentsList.map((i) => [
            String(i.value),
            language ? i.hi : i.name,
          ]),
        );

        const filtered = data
          .filter((item) => statusMap[item.status]) // O(1) lookup
          .map((item, index) => {
            const claimLabel =
              claimTypeMap[String(item.invest_claim_id)] || "Unknown";
            const installmentName = installmentMap[
              String(item.application_type)
            ]
              ? `(${installmentMap[String(item.application_type)]})`
              : "";

            return {
              sr_no: index + 1,
              applicant_name: userDetails?.user_details?.name,
              application_id: item.id,
              email: userDetails?.user_details?.email,
              tracking_number: item.tracking_number,
              updated_at: formatDate(item.updated_at),
              applied_incetive_claim: `${claimLabel} ${installmentName}`.trim(),
              applied_incetive_claim_value: item.invest_claim_id,
              status: statusMap[item.status] || "Unknown",
              status_value: item.status,
              stage: item.application_stage,
              application_type: item.application_type,
            };
          });

        setApplications(filtered);
      } catch (error) {
        console.error("Failed to fetch applications", error);
      }
    };

    fetchApplications();
  }, [status, userDetails?.user_details?.id]);

  const handleView = (row) => {
    navigate(`/application-draft/${row.application_id}`);
  };

  const handleDelete = (row) => {
    setSelectedRow(row);
    setShowDialog(true);
  };

  const confirmDelete = async () => {
    setShowDialog(false);
    try {
      const deleteResposne = await api.post(`/application/delete`, {
        application_id: selectedRow.application_id,
      });
      if (deleteResposne.status === 200) {
        setApplications((prev) =>
          prev.filter(
            (app) => app.application_id !== selectedRow.application_id,
          ),
        );
        setApiMessage({
          type: "success",
          message: "Application successfully deleted.",
        });
      } else {
        setApiMessage({ type: "error", message: "Error deleting Application" });
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const downloadPdf = (row) => {
    if (loadingPdf) return;

    setPdfData({
      applicationId: row.application_id,
      incentiveClaimId: row.applied_incetive_claim_value,
      installmentType: row.application_type,
      userId: userDetails?.user_details?.id, // pass user ID explicitly
    });

    setLoadingPdf(true);
  };

  const editApplication = (row) => {
    navigate(`/incentive-claim-form/${row.application_id}`);
  };

  return (
    <>
      {apiMessage && (
        <Message apiMessage={apiMessage} setApiMessage={setApiMessage} />
      )}

      <div className="flex h-12 nav-bg p-3 text-white font-semibold rounded-tr rounded-tl">
        {title}
      </div>

      <Table
        columns={applicationsTableColumns(
          handleView,
          handleDelete,
          downloadPdf,
          editApplication,
          language,
        )}
        data={applications}
        language={language}
      />

      <ConfirmDialog
        isOpen={showDialog}
        title={language ? "एप्लिकेशन हटाएं" : "Delete Application"}
        message={{
          en: "Are you sure you want to delete this application?",
          hi: "क्या आप वाकई इस एप्लिकेशन को हटाना चाहते हैं?",
        }}
        onConfirm={confirmDelete}
        onCancel={() => setShowDialog(false)}
        cancelVisible
        language={language}
      />

      {loadingPdf && (
        <Generating
          text={
            language
              ? "पीडीएफ जनरेट हो रही है, कृपया प्रतीक्षा करें..."
              : "Generating PDF, please wait..."
          }
        />
      )}

      <ApplicationPrint
        applicationId={pdfData?.applicationId}
        incentiveClaimId={pdfData?.incentiveClaimId}
        installmentType={pdfData?.installmentType}
        userId={pdfData?.userId}
        ref={pdfRef}
        isUserSide={true}
        onDone={() => {
          setLoadingPdf(false); // ✅ loader removed
          setPdfData(null); // ✅ cleanup
        }}
        language={language}
      />
    </>
  );
};

export default Applications;
